/** React Query hooks for custom audio content (clips), upload, and trash. */
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/mediaApi';
import type { CustomContentFilterParams, UpdateContentRequest } from '../types';

const PAGE_SIZE = 30;

export const contentKeys = {
  all: ['content'] as const,
  lists: () => [...contentKeys.all, 'clips'] as const,
  list: (params: CustomContentFilterParams) => [...contentKeys.lists(), params] as const,
  tags: () => [...contentKeys.all, 'tags'] as const,
  quota: () => [...contentKeys.all, 'quota'] as const,
  trash: () => [...contentKeys.all, 'trash'] as const,
};

export function useCustomContentList(params: CustomContentFilterParams) {
  return useInfiniteQuery({
    queryKey: contentKeys.list(params),
    queryFn: ({ pageParam }) => api.listCustomContent({ ...params, page: pageParam, pageSize: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
  });
}

export function useContentTags() {
  return useQuery({
    queryKey: contentKeys.tags(),
    queryFn: api.getContentTags,
    staleTime: 60_000,
  });
}

export function useContentQuota() {
  return useQuery({ queryKey: contentKeys.quota(), queryFn: api.getContentQuota, staleTime: 30_000 });
}

export function useUploadContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { file: File; title: string; tags?: string[]; onProgress?: (n: number) => void }) =>
      api.uploadCustomContent(vars.file, { title: vars.title, tags: vars.tags }, vars.onProgress),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contentKeys.lists() });
      qc.invalidateQueries({ queryKey: contentKeys.quota() });
      qc.invalidateQueries({ queryKey: contentKeys.tags() });
    },
  });
}

export function useUpdateContent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateContentRequest) => api.updateCustomContent(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contentKeys.lists() });
      qc.invalidateQueries({ queryKey: contentKeys.tags() });
    },
  });
}

/**
 * Delete a clip. On a 409 the underlying call throws `ApiError` (details =
 * DeleteConflictResponse); the caller should catch it, show affected playlists,
 * then re-call with `confirm: true`.
 */
export function useDeleteContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; confirm?: boolean }) => api.deleteCustomContent(vars.id, vars.confirm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contentKeys.lists() });
      qc.invalidateQueries({ queryKey: contentKeys.quota() });
      qc.invalidateQueries({ queryKey: contentKeys.trash() });
    },
  });
}

export function useTrashList() {
  return useQuery({
    queryKey: contentKeys.trash(),
    queryFn: () => api.listTrashContent({ pageSize: 100 }),
  });
}

export function useRestoreContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.restoreTrashContent(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contentKeys.trash() });
      qc.invalidateQueries({ queryKey: contentKeys.lists() });
      qc.invalidateQueries({ queryKey: contentKeys.quota() });
    },
  });
}
